import { m, useReducedMotion, useSpring } from "framer-motion";
import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { monotoneAreaPath, monotonePath, projectToBox, Point } from "../../../finance/curve";
import { MonthPoint } from "../../../finance/ledger";
import { hapticTick } from "../../../haptics";

const HEIGHT = 190;
/** Room above and below the curve for the dot's halo, which the path itself doesn't reserve. */
const PAD_Y = 10;
/** Side inset, so the first and last dots aren't halved by the edge of the screen. */
const PAD_X = 14;
/** How far a finger must travel sideways before a touch counts as a scrub and not a scroll. */
const SLOP = 6;
/** A finger held still this long is scrubbing too — the press-and-hold every stocks app has. */
const HOLD_MS = 200;
/** Stiff enough to stay under the finger, damped past critical so it never wobbles —
 *  a bouncing readout looks like a toy. Module scope: a fresh options object on
 *  every render is churn the spring doesn't need. */
const SPRING = { stiffness: 520, damping: 42, mass: 0.6 };

/**
 * The balance over time, scrubbable.
 *
 * The interaction is the whole point of this screen, so it's built the way a
 * native chart is rather than the way a web chart is:
 *
 *  - No hover. A tooltip that follows a mouse pointer is the single biggest
 *    "this is a webpage" tell on a phone, where there is no pointer to follow.
 *    The readout lives in the page header and is driven by touch alone.
 *  - The scrub is claimed, not guessed at. A touch becomes a scrub once it
 *    moves horizontally past a slop threshold or is simply held still.
 *    `touch-action: none` keeps the browser from racing that decision with
 *    its own angle heuristic and yanking the pointer away mid-scrub — the
 *    tradeoff is that once a touch turns out to be a vertical drag, the
 *    scroll it would have gotten for free has to be driven by hand instead.
 *  - The hairline springs between months rather than jumping. The value is
 *    discrete (one month, snapped) but the thing you're dragging is
 *    continuous, and moving it discretely feels like a dropped frame.
 *  - Lifting off doesn't just clear the state, it hands the chart back: the
 *    dot returns to today's balance, which is where the number in the header
 *    goes too.
 */
export function BalanceChart({
  points,
  activeIndex,
  onScrub,
  animateIn = true,
}: {
  points: MonthPoint[];
  /** The month being read, or null for "showing today". Owned by the page. */
  activeIndex: number | null;
  onScrub: (index: number | null) => void;
  animateIn?: boolean;
}) {
  const gradientId = useId();
  const reduceMotion = useReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  // Measured rather than a viewBox stretch: a stretched viewBox scales the
  // stroke with it, so the line would come out thicker on a wide phone than a
  // narrow one, and the dot would turn into an ellipse.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const plotHeight = HEIGHT - PAD_Y * 2;
  const plotWidth = Math.max(0, width - PAD_X * 2);
  const projected: Point[] =
    plotWidth > 0 ? projectToBox(points.map((p) => p.balanceOre), plotWidth, plotHeight) : [];
  const shifted = projected.map((p) => ({ x: p.x + PAD_X, y: p.y + PAD_Y }));
  const linePath = monotonePath(shifted);
  const areaPath = monotoneAreaPath(shifted, HEIGHT);

  // Reduced motion takes the draw-in with it, not just the transforms:
  // a 0.9s line being drawn across the screen is exactly the kind of motion
  // the setting is asking us not to perform. The finished curve is there on
  // the first frame instead.
  const drawIn = animateIn && !reduceMotion;

  const last = shifted[shifted.length - 1];
  const target = activeIndex != null ? shifted[activeIndex] : last;

  // The dot and hairline ride springs so they glide between months.
  //
  // The starting number is captured once and never passed again: `useSpring`
  // treats a numeric source as a live target and re-aims at it on every
  // render. The first render happens before the container has been measured,
  // so the number available then is 0 — and re-supplying it kept dragging
  // the dot back to the top-left corner of the chart mid-scrub. Everything
  // below drives the springs through `.set()` instead.
  const dotX = useSpring(0, SPRING);
  const dotY = useSpring(0, SPRING);
  // A dot springing in from the corner on first paint would read as a stray
  // object flying across the chart, so the very first position is a jump.
  const placed = useRef(false);
  const targetX = target?.x;
  const targetY = target?.y;

  // The springs are written onto the two groups as an SVG `transform`
  // attribute by hand, rather than handed to `m.g` as `style={{ x, y }}`.
  // framer-motion resolves a transform on an SVG element against a
  // `getBBox()` measurement taken when the element mounts, and these groups
  // mount inside an <svg> that has no width yet — the container hasn't been
  // measured — so the dot stayed pinned to the chart's top-left corner and
  // never followed the scrub. One attribute write per frame has no such
  // dependency, and is less work besides.
  const hairlineRef = useRef<SVGGElement>(null);
  const dotRef = useRef<SVGGElement>(null);
  useEffect(() => {
    const write = () => {
      const x = dotX.get();
      const y = dotY.get();
      hairlineRef.current?.setAttribute("transform", `translate(${x} 0)`);
      dotRef.current?.setAttribute("transform", `translate(${x} ${y})`);
    };
    write();
    const stopX = dotX.on("change", write);
    const stopY = dotY.on("change", write);
    return () => {
      stopX();
      stopY();
    };
  }, [dotX, dotY]);
  useEffect(() => {
    if (targetX == null || targetY == null) return;
    if (reduceMotion || !placed.current) {
      dotX.jump(targetX);
      dotY.jump(targetY);
      placed.current = true;
    } else {
      dotX.set(targetX);
      dotY.set(targetY);
    }
  }, [targetX, targetY, dotX, dotY, reduceMotion]);

  // ── Scrubbing ─────────────────────────────────────────────────────────
  const scrubbing = activeIndex != null;
  const gesture = useRef<{
    id: number;
    startX: number;
    startY: number;
    lastY: number;
    active: boolean;
    scrolling: boolean;
    holdTimer: number | null;
  } | null>(null);
  const lastHapticIndex = useRef<number | null>(null);
  // The nearest scrolling ancestor, so a gesture that turns out to be a
  // vertical drag can be handed to it by hand — see touchAction below.
  const scrollElRef = useRef<HTMLElement | null>(null);

  const indexAt = useCallback(
    (clientX: number) => {
      const el = containerRef.current;
      if (!el || points.length === 0) return 0;
      const box = el.getBoundingClientRect();
      // Against the plotted span, not the element: the curve is inset by
      // PAD_X, so measuring the full box would put the last month a few
      // pixels short of the right edge and make the end of the line
      // unreachable.
      const span = box.width - PAD_X * 2;
      const ratio = span <= 0 ? 0 : (clientX - box.left - PAD_X) / span;
      const index = Math.round(ratio * (points.length - 1));
      return Math.min(points.length - 1, Math.max(0, index));
    },
    [points.length]
  );

  const report = useCallback(
    (index: number) => {
      // One tick per month crossed, including the month you land on — the
      // detail that makes a scrub feel like it is moving over something
      // rather than through it. See haptics.ts for why this isn't just a
      // call to navigator.vibrate.
      if (lastHapticIndex.current !== index) {
        lastHapticIndex.current = index;
        hapticTick();
      }
      onScrub(index);
    },
    [onScrub]
  );

  const clearHold = () => {
    if (gesture.current?.holdTimer != null) {
      window.clearTimeout(gesture.current.holdTimer);
      gesture.current.holdTimer = null;
    }
  };

  const endGesture = useCallback(() => {
    clearHold();
    gesture.current = null;
    lastHapticIndex.current = null;
    onScrub(null);
  }, [onScrub]);

  const onPointerDown = (e: ReactPointerEvent) => {
    if (points.length < 2) return;
    const immediate = e.pointerType !== "touch";
    gesture.current = {
      id: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      lastY: e.clientY,
      active: immediate,
      scrolling: false,
      holdTimer: null,
    };
    if (immediate) {
      e.currentTarget.setPointerCapture(e.pointerId);
      report(indexAt(e.clientX));
      return;
    }
    // Touch: don't commit yet. Either the finger moves sideways (below), it
    // moves vertically (also below), or it stays put long enough to mean it.
    scrollElRef.current = containerRef.current?.closest<HTMLElement>("[data-scroll-container]") ?? null;
    const startX = e.clientX;
    const element = e.currentTarget;
    const pointerId = e.pointerId;
    gesture.current.holdTimer = window.setTimeout(() => {
      if (!gesture.current || gesture.current.id !== pointerId || gesture.current.scrolling) return;
      gesture.current.active = true;
      gesture.current.holdTimer = null;
      try {
        element.setPointerCapture(pointerId);
      } catch {
        // The pointer can be gone by the time the timer runs.
      }
      report(indexAt(startX));
    }, HOLD_MS);
  };

  const onPointerMove = (e: ReactPointerEvent) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;

    // Already handed off to the page: keep scrolling it by hand until the
    // finger lifts. See the touchAction note below for why this is on us.
    if (g.scrolling) {
      const scroller = scrollElRef.current;
      if (scroller) scroller.scrollTop -= e.clientY - g.lastY;
      g.lastY = e.clientY;
      return;
    }

    if (!g.active) {
      const dx = Math.abs(e.clientX - g.startX);
      const dy = Math.abs(e.clientY - g.startY);
      // Vertical wins outright: that's a page scroll starting. `touchAction`
      // is "none" on this element precisely so the browser can't jump ahead
      // of this decision and steal the pointer mid-scrub — which means it
      // also won't scroll the page for us once we've ceded the gesture, so
      // we drive the scroll container ourselves from here on.
      if (dy > dx && dy > SLOP) {
        clearHold();
        g.scrolling = true;
        const scroller = scrollElRef.current;
        if (scroller) scroller.scrollTop -= e.clientY - g.lastY;
        g.lastY = e.clientY;
        return;
      }
      if (dx < SLOP) return;
      clearHold();
      g.active = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    report(indexAt(e.clientX));
  };

  const onPointerEnd = (e: ReactPointerEvent) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    if (g.active) endGesture();
    else {
      clearHold();
      gesture.current = null;
    }
  };

  useEffect(() => clearHold, []);

  if (points.length === 0) return <div style={{ height: HEIGHT }} />;

  return (
    <div
      ref={containerRef}
      className="relative select-none"
      style={{
        height: HEIGHT,
        // none, not pan-y: pan-y lets the browser start scrolling on its own
        // read of the gesture's angle, racing ahead of the slop/hold check
        // below and stealing the pointer out from under a real scrub. With
        // "none" this element is the only thing deciding, and a vertical
        // drag is scrolled by hand in onPointerMove once recognized.
        touchAction: "none",
        WebkitTouchCallout: "none",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      // Long-pressing anything on iOS otherwise raises the copy/share callout
      // right on top of the chart you're scrubbing.
      onContextMenu={(e) => e.preventDefault()}
    >
      {width > 0 && (
        <svg width={width} height={HEIGHT} className="block overflow-visible" aria-hidden>
          <defs>
            {/* The area: pine at the line, gone by the floor. Two stops would
                band visibly across this much height, so it steps down. */}
            <linearGradient id={`${gradientId}-area`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="oklch(0.72 0.15 152)" stopOpacity="0.34" />
              <stop offset="45%" stopColor="oklch(0.66 0.14 155)" stopOpacity="0.13" />
              <stop offset="100%" stopColor="oklch(0.60 0.12 158)" stopOpacity="0" />
            </linearGradient>
            {/* The line warms as it travels, brass at the far end, so the most
                recent month is the brightest thing on the curve. */}
            <linearGradient id={`${gradientId}-line`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="oklch(0.62 0.10 160)" />
              <stop offset="55%" stopColor="oklch(0.78 0.16 152)" />
              <stop offset="100%" stopColor="oklch(0.86 0.13 128)" />
            </linearGradient>
            {/* The area is a closed shape, so it ends in a vertical wall at
                each end of the curve — a hard edge in a design that has just
                gone to some trouble not to have any. This mask dissolves the
                last few pixels at both ends; the line and the dot ride over
                it untouched, so "now" still ends somewhere definite. */}
            <linearGradient id={`${gradientId}-edges`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#000" />
              <stop offset="6%" stopColor="#fff" />
              <stop offset="94%" stopColor="#fff" />
              <stop offset="100%" stopColor="#000" />
            </linearGradient>
            <mask id={`${gradientId}-edgemask`}>
              <rect
                x={0}
                y={0}
                width={width}
                height={HEIGHT}
                fill={`url(#${gradientId}-edges)`}
              />
            </mask>
            <linearGradient id={`${gradientId}-hair`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="oklch(0.92 0.05 150)" stopOpacity="0" />
              <stop offset="22%" stopColor="oklch(0.92 0.05 150)" stopOpacity="0.72" />
              <stop offset="100%" stopColor="oklch(0.92 0.05 150)" stopOpacity="0.1" />
            </linearGradient>
          </defs>

          {/* The area follows the line in rather than arriving with it: the
              curve draws itself, then the ground fills in underneath. */}
          <m.path
            d={areaPath}
            fill={`url(#${gradientId}-area)`}
            mask={`url(#${gradientId}-edgemask)`}
            initial={drawIn ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          />
          {/* The glow is a second, fatter, fainter copy of the same stroke
              rather than a Gaussian blur filter. An SVG filter over a path
              that is being animated — drawn in on mount, and re-rasterised on
              every frame of a scrub — is one of the few things that will
              genuinely drop frames on a phone: the whole filter region has to
              be re-blurred each time. Two strokes cost one extra path. */}
          {[
            { width: 9, opacity: 0.1 },
            { width: 5, opacity: 0.16 },
            { width: 2.25, opacity: 1 },
          ].map((layer) => (
            <m.path
              key={layer.width}
              d={linePath}
              fill="none"
              stroke={`url(#${gradientId}-line)`}
              strokeWidth={layer.width}
              strokeOpacity={layer.opacity}
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={drawIn ? { pathLength: 0 } : false}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            />
          ))}

          {/* Hairline and dot. Rendered inside a group whose x is the spring,
              so both move as one object. */}
          <g
            ref={hairlineRef}
            opacity={scrubbing ? 1 : 0}
            style={{ transition: "opacity 180ms ease-out" }}
          >
            <line
              x1={0}
              x2={0}
              y1={0}
              y2={HEIGHT}
              stroke={`url(#${gradientId}-hair)`}
              strokeWidth={1.25}
            />
          </g>
          {/* Held back until the line has finished drawing in — appearing
              alongside it reads as the dot just popping into place rather
              than landing on a curve that's already there. */}
          <m.g
            ref={dotRef}
            initial={drawIn ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* The halo swells under the finger — the chart acknowledging the
                touch, which is the part that stops a scrub feeling inert. */}
            <m.circle
              // The attribute as well as the animation target: until
              // framer-motion's first frame lands there is no `r` on the
              // element at all, and SVG rejects the undefined outright.
              r={9}
              fill="oklch(0.86 0.15 148)"
              opacity={0.16}
              animate={{ r: scrubbing ? 13 : 9 }}
              transition={{ type: "spring", stiffness: 480, damping: 30 }}
            />
            {/* Same trick as the line: stacked discs instead of a blur. */}
            <circle r={7} fill="oklch(0.90 0.14 147)" opacity={0.22} />
            <circle r={4.25} fill="oklch(0.93 0.12 145)" />
            <circle r={1.6} fill="oklch(0.16 0.02 150)" />
          </m.g>
        </svg>
      )}
    </div>
  );
}

export const CHART_HEIGHT = HEIGHT;
